// ============================================================================
// Types
// ============================================================================

type TextColorMode = 'light' | 'dark'

export interface AdaptiveTextColorResult {
  textColor: string
  mode: TextColorMode
}

interface CacheEntry {
  result: AdaptiveTextColorResult
  timestamp: number
}

interface ImageCache {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
  url: string
}

// ============================================================================
// Constants
// ============================================================================

const LIGHT_COLOR = 'rgba(255, 255, 255, 1)'
const DARK_COLOR = 'rgba(0, 0, 0, 0.87)'
const LUMINANCE_THRESHOLD = 0.5
const CACHE_TTL_MS = 5000
const CACHE_MAX_SIZE = 100
const GRID_CELL_SIZE = 20

// ============================================================================
// Luminance Calculation (WCAG 2.0)
// ============================================================================

const calculateLuminance = (r: number, g: number, b: number): number => {
  const toLinear = (c: number): number => {
    const sRGB = c / 255
    return sRGB <= 0.03928
      ? sRGB / 12.92
      : Math.pow((sRGB + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

// ============================================================================
// Image Sampler Singleton
// ============================================================================

class ImageSampler {
  private static instance: ImageSampler | null = null

  private imageCache: Map<string, ImageCache> = new Map()
  private loadingPromises: Map<string, Promise<ImageCache | null>> = new Map()
  private resultCache: Map<string, CacheEntry> = new Map()

  private constructor() {}

  static getInstance(): ImageSampler {
    if (!ImageSampler.instance) {
      ImageSampler.instance = new ImageSampler()
    }
    return ImageSampler.instance
  }

  private pruneResultCache(): void {
    if (this.resultCache.size <= CACHE_MAX_SIZE) return

    const now = Date.now()
    const entries = Array.from(this.resultCache.entries())

    for (const [key, entry] of entries) {
      if (now - entry.timestamp > CACHE_TTL_MS) {
        this.resultCache.delete(key)
      }
    }

    if (this.resultCache.size > CACHE_MAX_SIZE) {
      const sorted = entries.sort((a, b) => a[1].timestamp - b[1].timestamp)
      const toRemove = sorted.slice(0, this.resultCache.size - CACHE_MAX_SIZE)
      for (const [key] of toRemove) {
        this.resultCache.delete(key)
      }
    }
  }

  private getCacheKey(
    imageUrl: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): string {
    const qX = Math.floor(x / GRID_CELL_SIZE) * GRID_CELL_SIZE
    const qY = Math.floor(y / GRID_CELL_SIZE) * GRID_CELL_SIZE
    const qW = Math.ceil(width / GRID_CELL_SIZE) * GRID_CELL_SIZE
    const qH = Math.ceil(height / GRID_CELL_SIZE) * GRID_CELL_SIZE
    return `${imageUrl}:${qX},${qY},${qW},${qH}`
  }

  private async loadImage(imageUrl: string): Promise<ImageCache | null> {
    // Return cached canvas if available
    const cached = this.imageCache.get(imageUrl)
    if (cached) return cached

    // Return existing loading promise if in progress
    const existingPromise = this.loadingPromises.get(imageUrl)
    if (existingPromise) return existingPromise

    // Start new load
    const loadPromise = new Promise<ImageCache | null>(resolve => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight

        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (ctx) {
          ctx.drawImage(img, 0, 0)
          const cache: ImageCache = { canvas, ctx, url: imageUrl }
          this.imageCache.set(imageUrl, cache)
          this.loadingPromises.delete(imageUrl)
          resolve(cache)
        } else {
          this.loadingPromises.delete(imageUrl)
          resolve(null)
        }
      }

      img.onerror = () => {
        this.loadingPromises.delete(imageUrl)
        resolve(null)
      }

      img.src = imageUrl
    })

    this.loadingPromises.set(imageUrl, loadPromise)
    return loadPromise
  }

  private sampleRegionLuminance(
    cache: ImageCache,
    imgX: number,
    imgY: number,
    imgWidth: number,
    imgHeight: number
  ): number {
    const { canvas, ctx } = cache

    const sampleX = Math.max(0, Math.min(Math.floor(imgX), canvas.width - 1))
    const sampleY = Math.max(0, Math.min(Math.floor(imgY), canvas.height - 1))
    const sampleWidth = Math.min(Math.ceil(imgWidth), canvas.width - sampleX)
    const sampleHeight = Math.min(Math.ceil(imgHeight), canvas.height - sampleY)

    if (sampleWidth <= 0 || sampleHeight <= 0) return 0.5

    try {
      const imageData = ctx.getImageData(
        sampleX,
        sampleY,
        sampleWidth,
        sampleHeight
      )
      const data = imageData.data
      const step = Math.max(16, Math.floor(data.length / 1000) * 4)

      let totalLuminance = 0
      let pixelCount = 0

      for (let i = 0; i < data.length; i += step) {
        totalLuminance += calculateLuminance(data[i], data[i + 1], data[i + 2])
        pixelCount += 1
      }

      return pixelCount > 0 ? totalLuminance / pixelCount : 0.5
    } catch {
      return 0.5
    }
  }

  async getTextColorForRect(
    imageUrl: string,
    viewportX: number,
    viewportY: number,
    width: number,
    height: number,
    imageContainerRect?: DOMRect
  ): Promise<AdaptiveTextColorResult> {
    const cache = await this.loadImage(imageUrl)

    if (!cache) {
      return { textColor: LIGHT_COLOR, mode: 'light' }
    }

    // Use provided container rect or default to viewport
    const containerWidth = imageContainerRect?.width ?? window.innerWidth
    const containerHeight = imageContainerRect?.height ?? window.innerHeight
    const containerX = imageContainerRect?.left ?? 0
    const containerY = imageContainerRect?.top ?? 0

    const imgWidth = cache.canvas.width
    const imgHeight = cache.canvas.height

    // Calculate scaling factor for background-size: cover
    const containerAspect = containerWidth / containerHeight
    const imageAspect = imgWidth / imgHeight

    let scale: number
    let offsetX = 0
    let offsetY = 0

    if (containerAspect > imageAspect) {
      scale = containerWidth / imgWidth
      offsetY = (containerHeight - imgHeight * scale) / 2
    } else {
      scale = containerHeight / imgHeight
      offsetX = (containerWidth - imgWidth * scale) / 2
    }

    // Adjust coordinates relative to container
    const relativeX = viewportX - containerX
    const relativeY = viewportY - containerY

    const imgX = (relativeX - offsetX) / scale
    const imgY = (relativeY - offsetY) / scale
    const imgSampleWidth = width / scale
    const imgSampleHeight = height / scale

    // Focus on bottom portion where text appears
    const textRegionY = imgY + imgSampleHeight * 0.5
    const textRegionHeight = imgSampleHeight * 0.5

    // Check cache
    const cacheKey = this.getCacheKey(
      imageUrl,
      imgX,
      textRegionY,
      imgSampleWidth,
      textRegionHeight
    )
    const cached = this.resultCache.get(cacheKey)
    const now = Date.now()

    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      return cached.result
    }

    // Calculate luminance
    const luminance = this.sampleRegionLuminance(
      cache,
      imgX,
      textRegionY,
      imgSampleWidth,
      textRegionHeight
    )

    const mode: TextColorMode =
      luminance > LUMINANCE_THRESHOLD ? 'dark' : 'light'
    const result: AdaptiveTextColorResult = {
      textColor: mode === 'light' ? LIGHT_COLOR : DARK_COLOR,
      mode
    }

    // Store in cache
    this.resultCache.set(cacheKey, { result, timestamp: now })
    this.pruneResultCache()

    return result
  }

  /**
   * Invalidates cached data for a specific image URL
   */
  invalidateImage(imageUrl: string): void {
    this.imageCache.delete(imageUrl)
    // Remove all result cache entries for this image
    for (const key of this.resultCache.keys()) {
      if (key.startsWith(imageUrl + ':')) {
        this.resultCache.delete(key)
      }
    }
  }

  /**
   * Clears all caches
   */
  clearAll(): void {
    this.imageCache.clear()
    this.resultCache.clear()
    this.loadingPromises.clear()
  }
}

// ============================================================================
// Public API
// ============================================================================

const DEFAULT_RESULT: AdaptiveTextColorResult = {
  textColor: LIGHT_COLOR,
  mode: 'light'
}

/**
 * Gets adaptive text color for an HTML element based on a background image.
 *
 * @param element - The HTML element to calculate text color for
 * @param imageUrl - URL of the background image to sample
 * @param containerRect - Optional rect of the container with the background image
 * @returns Promise resolving to the adaptive text color result
 */
export async function getAdaptiveTextColor(
  element: HTMLElement,
  imageUrl: string,
  containerRect?: DOMRect
): Promise<AdaptiveTextColorResult> {
  const rect = element.getBoundingClientRect()
  return getAdaptiveTextColorForRect(
    imageUrl,
    rect.left,
    rect.top,
    rect.width,
    rect.height,
    containerRect
  )
}

/**
 * Gets adaptive text color for a viewport rectangle based on a background image.
 *
 * @param imageUrl - URL of the background image to sample
 * @param x - Viewport X coordinate
 * @param y - Viewport Y coordinate
 * @param width - Width of the region
 * @param height - Height of the region
 * @param containerRect - Optional rect of the container with the background image
 * @returns Promise resolving to the adaptive text color result
 */
export async function getAdaptiveTextColorForRect(
  imageUrl: string,
  x: number,
  y: number,
  width: number,
  height: number,
  containerRect?: DOMRect
): Promise<AdaptiveTextColorResult> {
  try {
    const sampler = ImageSampler.getInstance()
    return await sampler.getTextColorForRect(
      imageUrl,
      x,
      y,
      width,
      height,
      containerRect
    )
  } catch {
    return DEFAULT_RESULT
  }
}

/**
 * Preloads an image for faster subsequent color calculations.
 *
 * @param imageUrl - URL of the image to preload
 */
export async function preloadImage(imageUrl: string): Promise<void> {
  const sampler = ImageSampler.getInstance()
  await sampler.getTextColorForRect(imageUrl, 0, 0, 1, 1)
}

/**
 * Invalidates cached data for a specific image URL.
 * Call this when the background image changes.
 *
 * @param imageUrl - URL of the image to invalidate
 */
export function invalidateImageCache(imageUrl: string): void {
  ImageSampler.getInstance().invalidateImage(imageUrl)
}

/**
 * Clears all cached image and result data.
 */
export function clearAdaptiveTextColorCache(): void {
  ImageSampler.getInstance().clearAll()
}
